import * as Yup from 'yup';

import { PROFILE_FIELD_OTHER_VALUE } from '@utils/backend-api/profileSettings';

interface FirstPartyData {
  isRequired: boolean;
  requiredMessage?: string;
}

export type FirstPartyDataField =
  | 'companyName'
  | 'companySize'
  | 'industry'
  | 'jobFunction'
  | 'jobLevel'
  | 'postalCode'
  | 'subIndustry'
  | 'subJobFunction';

interface FirstPartyDataSchemaOptions {
  isRequired?: boolean;
  fields?: FirstPartyDataField[];
}

export const autoCompleteField = ({ isRequired, requiredMessage }: FirstPartyData) =>
  Yup.object().shape({
    label: Yup.string(),
    value: isRequired ? Yup.string().required(requiredMessage) : Yup.string(),
  });

const firstPartyDataSchema = ({ isRequired = true, fields }: FirstPartyDataSchemaOptions = {}) => {
  const allFields: Record<string, Yup.AnySchema> = {
    companySize: Yup.object().shape({
      label: Yup.string(),
      value: Yup.string(),
    }),
    customIndustry: Yup.object().when('industry', (industry) => {
      return Yup.object().shape({
        label: Yup.string(),
        value: Yup.lazy((value) => {
          if (industry && industry.value === PROFILE_FIELD_OTHER_VALUE) {
            return typeof value === 'number'
              ? Yup.number().min(1, 'Please enter an industry')
              : Yup.string().required('Please enter an industry');
          }
          return Yup.mixed();
        }),
      });
    }),
    customJobFunction: Yup.object().when('jobFunction', (jobFunction) => {
      return Yup.object().shape({
        label: Yup.string(),
        value: Yup.lazy((value) => {
          if (jobFunction && jobFunction.value === PROFILE_FIELD_OTHER_VALUE) {
            return typeof value === 'number'
              ? Yup.number().min(1, 'Please enter a job function')
              : Yup.string().required('Please enter a job function');
          }
          return Yup.mixed();
        }),
      });
    }),
    customJobLevel: Yup.object().when('jobLevel', (jobLevel) => {
      return Yup.object().shape({
        label: Yup.string(),
        value: Yup.lazy((value) => {
          if (jobLevel && jobLevel.value === PROFILE_FIELD_OTHER_VALUE) {
            return typeof value === 'number'
              ? Yup.number().min(1, 'Please enter a job level')
              : Yup.string().required('Please enter a job level');
          }
          return Yup.mixed();
        }),
      });
    }),
    customSubIndustry: Yup.object().when('subIndustry', (subIndustry) => {
      return Yup.object().shape({
        label: Yup.string(),
        value: Yup.lazy((value) => {
          if (subIndustry && subIndustry.value === PROFILE_FIELD_OTHER_VALUE) {
            return typeof value === 'number'
              ? Yup.number().min(1, 'Please enter a sub industry')
              : Yup.string().required('Please enter a sub industry');
          }
          return Yup.mixed();
        }),
      });
    }),
    customSubJobFunction: Yup.object().when('subJobFunction', (subJobFunction) => {
      return Yup.object().shape({
        label: Yup.string(),
        value: Yup.lazy((value) => {
          if (subJobFunction && subJobFunction.value === PROFILE_FIELD_OTHER_VALUE) {
            return typeof value === 'number'
              ? Yup.number().min(1, 'Please enter a sub job function')
              : Yup.string().required('Please enter a sub job function');
          }
          return Yup.mixed();
        }),
      });
    }),
    industry: autoCompleteField({ isRequired, requiredMessage: 'Please select an industry' }),
    jobFunction: autoCompleteField({ isRequired, requiredMessage: 'Please select a job function' }),
    jobLevel: autoCompleteField({ isRequired, requiredMessage: 'Please select a job level' }),
    postalCode: Yup.string().min(5, 'Please enter a valid postal code'),
    subIndustry: autoCompleteField({ isRequired, requiredMessage: 'Please select a sub industry' }),
    subJobFunction: autoCompleteField({
      isRequired,
      requiredMessage: 'Please select a sub job function',
    }),
  };

  // If fields are specified, only include those fields and their dependencies
  if (fields && fields.length > 0) {
    const filteredFields: Record<string, Yup.AnySchema> = {};

    // Helper to check if a field should be included
    const shouldInclude = (fieldName: string): boolean => {
      // Always include custom fields if their parent field is included
      if (fieldName === 'customIndustry' && fields.includes('industry')) return true;
      if (fieldName === 'customJobFunction' && fields.includes('jobFunction')) return true;
      if (fieldName === 'customJobLevel' && fields.includes('jobLevel')) return true;
      if (fieldName === 'customSubIndustry' && fields.includes('subIndustry')) return true;
      if (fieldName === 'customSubJobFunction' && fields.includes('subJobFunction')) return true;

      // Include the field if it's in the fields array
      return fields.includes(fieldName as FirstPartyDataField);
    };

    Object.keys(allFields).forEach((key) => {
      if (shouldInclude(key)) {
        filteredFields[key] = allFields[key];
      }
    });

    return filteredFields;
  }

  // If no fields specified, return all fields (backward compatible)
  return allFields;
};

export default firstPartyDataSchema;
